/* eslint-disable no-unused-vars */
import React, { useMemo, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Label } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

import AppButton from 'components/App/Button';
import AppInputValidation from 'components/App/InputValidation';
import Authentication from 'components/App/Authentication';
import AuthTermsNotice from 'components/App/AuthTermsNotice';
import Meta from 'components/App/Meta';
import images from 'assets/images';

import { loginAdminValidation } from 'utils/validate';
import { LOGIN_TYPE, LOCATIONS, storageKey } from 'constants/index';
import authApi from 'api/authApi';

import ConfirmPassword from './ConfirmPassword';
import NewPassword from './NewPassword';
import styles from './ForgotPassword.module.scss';

const STEPS = {
  EMAIL: 0,
  OTP: 1,
  NEW_PASSWORD: 2,
};

const ForgotPasswordPage = (props) => {
  const {
    actionForgotPassword,
    forgotPasswordLoading,
  } = props;

  const history = useHistory();
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const loginType = useMemo(() => {
    const type = localStorage.getItem(storageKey.LOGIN_TYPE);
    return type === LOGIN_TYPE.EXTRANET ? LOGIN_TYPE.EXTRANET : LOGIN_TYPE.INTRANET;
  }, []);

  const validationStep1 = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: loginAdminValidation.email,
    }),
    onSubmit: async (values) => {
      if (loginType === LOGIN_TYPE.EXTRANET) {
        try {
          setLoading(true);
          await authApi.forgotPasswordExtranet({ email: values.email });
          setEmail(values.email);
          setStep(STEPS.OTP);
        } catch (error) {
          toast.error(
            error.response?.data?.message || 'Có lỗi xảy ra, vui lòng thử lại!',
          );
        } finally {
          setLoading(false);
        }
      } else {
        actionForgotPassword({ user: values, history });
      }
    },
  });

  const onChangeEmail = useCallback((e) => {
    validationStep1.setValues((prev) => ({
      ...prev,
      email: e.target.value.trim(),
    }));
  }, [validationStep1]);

  const handleSubmitOtp = useCallback(async (codeValue) => {
    if (loginType === LOGIN_TYPE.EXTRANET) {
      try {
        setLoading(true);
        await authApi.verifyCodeExtranet({ code: codeValue, email });
        setCode(codeValue);
        setStep(STEPS.NEW_PASSWORD);
      } catch (error) {
        toast.error(
          error.response?.data?.message || 'Mã xác thực không hợp lệ!',
        );
      } finally {
        setLoading(false);
      }
    } else {
      history.push(LOCATIONS.LOGIN);
    }
  }, [loginType, history, email]);

  const handleSubmitNewPassword = useCallback(async (values) => {
    if (loginType === LOGIN_TYPE.EXTRANET) {
      try {
        setLoading(true);
        await authApi.resetPasswordExtranet({
          email,
          code,
          password: values.password,
          confirmPassword: values.confirmPassword,
        });
        toast.success('Đặt lại mật khẩu thành công!');
        history.push(LOCATIONS.LOGIN);
      } catch (error) {
        toast.error(
          error.response?.data?.message || 'Có lỗi xảy ra, vui lòng thử lại!',
        );
      } finally {
        setLoading(false);
      }
    } else {
      history.push(LOCATIONS.LOGIN);
    }
  }, [loginType, history, email, code]);

  const handleResendOtp = useCallback(async () => {
    if (loginType === LOGIN_TYPE.EXTRANET && email) {
      try {
        setLoading(true);
        await authApi.forgotPasswordExtranet({ email });
        toast.success('Mã OTP mới đã được gửi thành công!');
      } catch (error) {
        toast.error(
          error.response?.data?.message || 'Có lỗi xảy ra, vui lòng thử lại!',
        );
      } finally {
        setLoading(false);
      }
    }
  }, [loginType, email]);

  const renderEmailForm = () => (
    <form onSubmit={(e) => { e.preventDefault(); validationStep1.handleSubmit(); }}>
      <div className={styles.FormGroup}>
        <Label className={styles.FormLabel}>
          Email đăng ký
          <code>*</code>
        </Label>

        <AppInputValidation
          inputClassName={styles.FormInput}
          placeholder="Nhập tên đăng nhập"
          name="email"
          validation={validationStep1}
          isCustomOnChange
          onChange={onChangeEmail}
        />
      </div>

      <AppButton
        classes={styles.SubmitBtn}
        loading={forgotPasswordLoading || loading}
        type="submit"
      >
        KẾ TIẾP
      </AppButton>

      <AuthTermsNotice className={styles.Terms} />
    </form>
  );

  // If we are at the new password step (only for EXTRANET)
  if (loginType === LOGIN_TYPE.EXTRANET && step === STEPS.NEW_PASSWORD) {
    return (
      <NewPassword
        loginType={loginType}
        forgotPasswordLoading={forgotPasswordLoading || loading}
        onSubmitNewPassword={handleSubmitNewPassword}
      />
    );
  }

  // If we are at the OTP verification step (only for EXTRANET)
  if (loginType === LOGIN_TYPE.EXTRANET && step === STEPS.OTP) {
    return (
      <ConfirmPassword
        loginType={loginType}
        forgotPasswordLoading={forgotPasswordLoading || loading}
        onSubmitOtp={handleSubmitOtp}
        onResendOtp={handleResendOtp}
      />
    );
  }

  // Extranet flow step 1: Email Form
  if (loginType === LOGIN_TYPE.EXTRANET) {
    return (
      <div className={styles.Container}>
        <Meta name="Quên mật khẩu" />

        <header className={styles.Header}>
          <div className={styles.HeaderContent}>
            <img
              src={images.logoBookeseFull.default}
              alt="logo-bookese"
              className={styles.HeaderLogo}
            />
          </div>
        </header>

        <main className={styles.Main}>
          <div className={styles.Card}>
            <div className={styles.CardHeader}>
              <h1 className={styles.CardTitle}>Quên mật khẩu</h1>
              <p className={styles.CardDescription}>
                Khôi phục và thiết lập lại mật khẩu
              </p>
            </div>
            <div className={styles.CardBody}>
              {renderEmailForm()}
            </div>
          </div>
        </main>

        <footer className={styles.Footer}>
          <p className={styles.Copyright}>
            © 2022 Bookese. Design and Develop by Wiicamp
          </p>
        </footer>
      </div>
    );
  }

  // Intranet fallback - Email Form inside Authentication component
  return (
    <>
      <Meta name="Quên mật khẩu" />

      <Authentication
        title="Quên mật khẩu"
        description="Khôi phục và thiết lập lại mật khẩu"
      >
        <div className={styles.Page}>
          {renderEmailForm()}
        </div>
      </Authentication>
    </>
  );
};

ForgotPasswordPage.propTypes = {
  actionForgotPassword: PropTypes.func.isRequired,
  forgotPasswordLoading: PropTypes.bool.isRequired,
};

export default ForgotPasswordPage;
